
import type { TutorialDefinition, TutorialProgress } from '../../types';
import { CheckIcon, PlayIcon } from '../../icons';

interface TutorialCardProps {
  tutorial: TutorialDefinition;
  progress?: TutorialProgress;
  onStart: (id: string) => void;
}

export function TutorialCard({ tutorial, progress, onStart }: TutorialCardProps) {
  const isCompleted = progress?.completed ?? false;
  const isInProgress =
    progress != null && !progress.completed && !progress.dismissed && progress.currentStep > 0;
  const stepsDone = progress?.currentStep ?? 0;
  const totalSteps = tutorial.steps.length;
  const pct = totalSteps > 0 ? Math.round((stepsDone / totalSteps) * 100) : 0;

  return (
    <div
      style={{
        background: 'var(--sw-bg-surface, #22223b)',
        borderRadius: 'var(--sw-border-radius, 8px)',
        padding: '14px 16px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        border: '1px solid var(--sw-border, #2d2d44)',
        transition: 'border-color 0.15s',
      }}
    >
      {/* Top row: title + status */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span
          style={{
            flex: 1,
            fontWeight: 600,
            fontSize: '14px',
            color: 'var(--sw-text-primary, #e0e0e0)',
          }}
        >
          {tutorial.title}
        </span>
        {isCompleted && (
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '4px',
              color: '#22c55e',
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            <CheckIcon size={14} />
            Done
          </span>
        )}
      </div>

      {/* Description */}
      <p
        style={{
          margin: 0,
          fontSize: '13px',
          color: 'var(--sw-text-secondary, #9a9ab0)',
          lineHeight: 1.5,
        }}
      >
        {tutorial.description}
      </p>

      {/* Meta row: duration + steps */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: '12px',
          color: 'var(--sw-text-secondary, #9a9ab0)',
        }}
      >
        <span>{tutorial.duration}</span>
        <span>{totalSteps} steps</span>
      </div>

      {/* Progress bar (only if in-progress) */}
      {isInProgress && (
        <div
          style={{
            height: '4px',
            borderRadius: '2px',
            background: 'var(--sw-border, #2d2d44)',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              height: '100%',
              width: `${pct}%`,
              background: 'var(--sw-accent-primary, #4cc9f0)',
              borderRadius: '2px',
              transition: 'width 0.3s ease',
            }}
          />
        </div>
      )}

      {/* Action button */}
      <button
        type="button"
        onClick={() => onStart(tutorial.id)}
        style={{
          alignSelf: 'flex-start',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          padding: '6px 14px',
          minWidth: '48px',
          minHeight: '48px',
          borderRadius: 'var(--sw-border-radius, 8px)',
          border: '1px solid var(--sw-accent-primary, #4cc9f0)',
          background: isCompleted ? 'transparent' : 'var(--sw-accent-primary, #4cc9f0)',
          color: isCompleted ? 'var(--sw-accent-primary, #4cc9f0)' : '#fff',
          cursor: 'pointer',
          fontSize: '13px',
          fontWeight: 500,
          transition: 'opacity 0.15s',
        }}
      >
        <PlayIcon size={12} />
        {isCompleted ? 'Replay' : isInProgress ? 'Resume' : 'Start'}
      </button>
    </div>
  );
}
